import ReactECharts from 'echarts-for-react';

/**
 * AutonomyLadderChart_4 — CONTENT alternative to AutonomyLadderChart.
 *
 * Angle: "oversight never disappears". Same real values as the original
 * (autonomy [0, 60, 90] vs human review [100, 40, 10] across
 * MANUAL / SEMI_AUTO / FULL_AUTO), re-cut as 100% stacked horizontal bars
 * per mode. Because each mode's autonomy + review shares sum to 100, the
 * stack makes the residual human-review slice the finding: even FULL_AUTO
 * keeps a 10% human gate.
 *
 * Chart-type-first: part-of-whole per category → 100% stacked horizontal bar.
 * Opaque white background, light editorial styling (content-only variant;
 * theme is chosen separately).
 */

const BRAND = {
  teal: '#0AB3BF',
  slate: '#334155',
  muted: '#64748B',
  surface: '#FFFFFF',
};

const MODES = ['MANUAL', 'SEMI_AUTO', 'FULL_AUTO'];
const AUTONOMY = [0, 60, 90]; // what the bot acts on its own (from AutonomyLadderChart)
const REVIEW = [100, 40, 10]; // what a human still gates (from AutonomyLadderChart)

/** Shares derived visibly: each mode's two values sum to 100, so share = value / mode total * 100. */
const MODE_TOTALS = MODES.map((_, i) => AUTONOMY[i] + REVIEW[i]);
const AUTONOMY_SHARE = AUTONOMY.map((v, i) => (v / MODE_TOTALS[i]) * 100);
const REVIEW_SHARE = REVIEW.map((v, i) => (v / MODE_TOTALS[i]) * 100);

/** ONE shared percentage formatter — tooltip, labels, and axis all use it. */
const formatPct = (v: number) => `${Math.round(v)}%`;

export const AutonomyLadderChart_4 = ({ height = 280 }: { height?: number }) => {
  const option = {
    backgroundColor: BRAND.surface,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const i = params[0].dataIndex;
        return `${MODES[i]}<br/>${params
          .map((p) => `${p.marker} ${p.seriesName}: <b>${formatPct(p.value)}</b> of actions`)
          .join('<br/>')}`;
      },
    },
    legend: {
      top: 8,
      textStyle: { color: BRAND.slate, fontSize: 13 },
      data: ['Autonomy (bot acts)', 'Human review (gates)'],
    },
    grid: { left: 92, right: 24, top: 48, bottom: 36 },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: 'share of actions (%)',
      nameTextStyle: { color: BRAND.muted, fontSize: 11 },
      axisLabel: { color: BRAND.muted, fontSize: 12, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: BRAND.slate, opacity: 0.15 } },
    },
    yAxis: {
      type: 'category',
      data: MODES,
      axisLine: { lineStyle: { color: BRAND.slate } },
      axisLabel: { color: BRAND.slate, fontSize: 13, fontWeight: 700 },
      axisTick: { show: false },
    },
    series: [
      {
        name: 'Autonomy (bot acts)',
        type: 'bar',
        stack: 'share',
        barWidth: '55%',
        data: AUTONOMY_SHARE.map((v) => ({
          value: v,
          itemStyle: { color: '#CBD5E1' }, // muted slate — the backdrop, not the finding
        })),
        label: {
          show: true,
          position: 'inside',
          color: BRAND.slate,
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => (p.value > 0 ? formatPct(p.value) : ''),
        },
      },
      {
        name: 'Human review (gates)',
        type: 'bar',
        stack: 'share',
        barWidth: '55%',
        data: REVIEW_SHARE.map((v) => ({
          value: v,
          itemStyle: { color: BRAND.teal, borderRadius: [0, 6, 6, 0] }, // accent — the finding
        })),
        label: {
          show: true,
          position: 'inside',
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 1. Oversight never disappears: stacked to 100%, each mode shows autonomy (grey) against the human-review
        share that remains (teal). The teal slice shrinks from all of MANUAL to a sliver at FULL_AUTO — but never
        reaches zero: one in ten actions still passes a human gate. Hover for values.
      </p>
    </div>
  );
};
